import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { IBalanceHistoryItem } from 'common/components/PersonProfile/interfaces';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { DialogComponent, Table } from 'common/components/index';
import commonMessages from 'common/messages/messages';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import tableHeaders from 'common/messages/tableHeaders';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import { Typography, useTheme } from '@material-ui/core';
import { BalanceType } from '../../constants';
import { formatPrice } from 'common/utils';
import { makeTableParams } from 'common/utils/http';
import { TableOrderByParams } from 'common/constants';

const useStyles = makeStyles({
  invoiceNumberCol: {
    cursor: 'pointer',
    display: 'inline-block',
  },
});

interface IProps {
  onClose: () => void;
  balanceHistory: IBalanceHistoryItem[];
  balanceHistoryLoading: boolean;
  fetchBalanceHistory: (tableParams: ITableParams) => void;
  setSelectedInvoiceId: (id: string) => void;
}

const BalanceHistoryModal = ({
  onClose,
  balanceHistory = [],
  balanceHistoryLoading,
  fetchBalanceHistory,
  setSelectedInvoiceId,
}: IProps): JSX.Element => {
  const [timezoneMoment] = useTimezoneMoment();
  const theme = useTheme();
  const location = useLocation();
  const classes = useStyles();

  const [tableParams, setTableParams] = useState<ITableParams>(() => {
    return makeTableParams([], location.search, {
      orderBy: TableOrderByParams.CREATED_DATE,
      order: 'desc',
    });
  });

  const onChangeTableProps = useCallback((tableProps: ITableParams): void => {
    setTableParams(tableProps);
  }, []);

  const headerOptions = useMemo(
    (): IHeadCell[] => [
      {
        id: 'invoice_number',
        label: <FormattedMessage {...tableHeaders.invoiceNumber} />,
        sort: true,
      },
      {
        id: 'amount',
        label: <FormattedMessage {...tableHeaders.amount} />,
        align: 'right',
        sort: true,
      },
      {
        id: 'type',
        label: <FormattedMessage {...tableHeaders.type} />,
        sort: true,
      },
      {
        id: 'created_date',
        label: <FormattedMessage {...tableHeaders.date} />,
        sort: true,
      },
    ],
    [],
  );

  const tableRows: ITableRow[] = balanceHistory.map(item => {
    const isDebitType = item.type === BalanceType.DEBIT;

    return {
      id: item.id.toString(),
      cells: [
        {
          label: '',
          cellComponent: (
            <Typography
              variant="h6"
              color="primary"
              className={classes.invoiceNumberCol}
              onClick={() => setSelectedInvoiceId(item.invoiceId)}
            >
              {item.invoiceNumber}
            </Typography>
          ),
        },
        {
          label: formatPrice(item.amount),
          align: 'right',
        },
        {
          label: '',
          cellComponent: (
            <Typography
              variant="h6"
              color="primary"
              style={{
                color: isDebitType ? theme.palette.error.main : theme.palette.primary.main,
              }}
            >
              <FormattedMessage
                {...(isDebitType ? commonMessages.debitLabel : commonMessages.creditLabel)}
              />
            </Typography>
          ),
        },
        {
          label: `${timezoneMoment(item.createdAt).format(DEFAULT_DATE_TIME_FORMAT)}`,
        },
      ],
    };
  });

  useEffect(() => {
    fetchBalanceHistory(tableParams);
  }, [fetchBalanceHistory, tableParams]);

  return (
    <DialogComponent
      isOpen
      title={<FormattedMessage {...commonMessages.accountCreditHistoryTitle} />}
      onClose={onClose}
      size="lg"
      hideFooterButtons
    >
      <Table
        rows={tableRows}
        headerOptions={headerOptions}
        tableParams={tableParams}
        onChangeParams={onChangeTableProps}
        initialOrder="desc"
        initialOrderBy={TableOrderByParams.CREATED_DATE.toLowerCase()}
        hideSearchInput
        hidePagination
        isLoading={balanceHistoryLoading}
      />
    </DialogComponent>
  );
};

export default BalanceHistoryModal;
